import styled from '@emotion/styled';
import { Health, HealthProps } from '@tecton/ComponentRedesign';
import moment from 'moment-timezone';
import { FC } from 'react';
import { ServingState } from '../../../../types/tecton_proto/data/serving_status';
import { ServingStatusEvent, ServingStatusPanelTableDatum } from '../../../servingStatus/types';
import { TectonDateTimeFormat } from '../utils';

interface ServingStateTooltipProps {
  batchEvent?: ServingStatusEvent;
  streamingDatum?: ServingStatusPanelTableDatum;
  x: number;
  y: number;
}

const TooltipBody = styled.div`
  padding: ${({ theme }) => theme.padding.s} ${({ theme }) => theme.padding.xs};
  background-color: ${({ theme }) => theme.colors.emptyShade};
  box-shadow: ${({ theme }) => theme.shadow.l};
`;

const ResultDescription = styled.div`
  font-size: ${({ theme }) => theme.font.fontSizes.xs};
  padding: ${({ theme }) => theme.padding.xs} ${({ theme }) => theme.padding.l};
`;

const ServingStatusTooltip: FC<ServingStateTooltipProps> = ({ batchEvent, streamingDatum, x, y }) => {
  const height = 50;
  const width = 500;

  const statusMap: Record<ServingState, HealthProps> = {
    [ServingState.SERVING_STATE_DISABLED]: { variant: 'warning', label: 'Disabled' },
    [ServingState.SERVING_STATE_ERROR]: { variant: 'failure', label: 'Error' },
    [ServingState.SERVING_STATE_NOT_ENOUGH_DATA]: { variant: 'neutral', label: 'Not Enough Data' },
    [ServingState.SERVING_STATE_OK]: { variant: 'healthy', label: 'Okay' },
    [ServingState.SERVING_STATE_PENDING]: { variant: 'neutral', label: 'Pending' },
    [ServingState.SERVING_STATE_RUNNING]: { variant: 'active', label: 'Running' },
    [ServingState.SERVING_STATE_UNSPECIFIED]: { variant: 'warning', label: 'Unspecified' },
  };

  if (batchEvent) {
    return (
      <>
        <foreignObject
          x={x - width / 2}
          width={width}
          height={height}
          y={y}
          style={{ overflow: 'visible', pointerEvents: 'none', backgroundColor: 'white' }}
        >
          <TooltipBody>
            <Health {...statusMap[batchEvent.status]} />
            <ResultDescription>
              Between {TectonDateTimeFormat(moment(batchEvent.startDate))} and{' '}
              {TectonDateTimeFormat(moment(batchEvent.endDate))}
            </ResultDescription>
            <div>
              <svg
                height={15}
                width={30}
                style={{ position: 'absolute', bottom: '-30px', left: 'calc(50%)', overflow: 'visible' }}
              >
                <polygon points="-15,0 0,15 15,0" fill="white" />
              </svg>
            </div>
          </TooltipBody>
        </foreignObject>
      </>
    );
  }

  if (!streamingDatum || streamingDatum.streamingStatus === undefined) {
    return <></>;
  }

  return (
    <>
      <foreignObject
        x={x - width / 2}
        width={width}
        height={height}
        y={y}
        style={{ overflow: 'visible', pointerEvents: 'none', backgroundColor: 'white' }}
      >
        <TooltipBody>
          <Health {...statusMap[streamingDatum.streamingStatus]} />
          <ResultDescription>
            Since {TectonDateTimeFormat(moment(streamingDatum.streamingStartDate))}. Updated{' '}
            {TectonDateTimeFormat(moment(streamingDatum.streamingMostRecentDate))}
          </ResultDescription>
          <div>
            <svg
              height={15}
              width={30}
              style={{ position: 'absolute', bottom: '-30px', left: 'calc(50%)', overflow: 'visible' }}
            >
              <polygon points="-15,0 0,15 15,0" fill="white" />
            </svg>
          </div>
        </TooltipBody>
      </foreignObject>
    </>
  );
};

export default ServingStatusTooltip;
